import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

interface MenuItem {
  icon: string;
  label: string;
  action: string;
}

const menuItems: MenuItem[] = [
  { icon: 'ri-camera-line', label: 'Snap & Solve', action: 'scan' },
  { icon: 'ri-questionnaire-line', label: 'Practice', action: 'Quizify' },
  { icon: 'ri-stack-line', label: 'Study Cards', action: 'Flashcards' },
  { icon: 'ri-calculator-line', label: 'Smart Calculator', action: 'Calculator' },
  { icon: 'ri-file-text-line', label: 'Quick Learn', action: 'TLDR' },
];

interface ExpandableMenuProps {
  theme: 'dark' | 'light';
}

export default function ExpandableMenu({ theme }: ExpandableMenuProps) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    },
    []
  );

  useEffect(() => {
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [open, handleClickOutside]);

  const handleMenuItemClick = (action: string) => {
    setOpen(false);
    navigate(action === 'scan' ? '/scan/start' : `/?tool=${encodeURIComponent(action)}`);
  };

  const isDark = theme === 'dark';

  return (
    <div ref={containerRef} className="relative flex-shrink-0" onKeyDown={(event) => { if (event.key === 'Escape') { setOpen(false); containerRef.current?.querySelector('button')?.focus(); } }}>
      <button
        onClick={() => setOpen(!open)}
        className={`w-8 h-8 rounded-full flex items-center justify-center cursor-pointer transition-all duration-200 active:scale-[0.88] ${
          open
            ? isDark
              ? 'bg-white/15 text-[#F5F5F7]'
              : 'bg-black/10 text-black'
            : isDark
              ? 'bg-white/[0.04] hover:bg-white/[0.08] text-[#6E6E73] hover:text-[#A1A1A6]'
              : 'bg-black/[0.04] hover:bg-black/[0.08] text-black/40 hover:text-black/70'
        }`}
        style={{ touchAction: 'manipulation' }}
        aria-label="Open menu"
        aria-expanded={open}
        type="button"
      >
        <div className="w-4 h-4 flex items-center justify-center">
          <i className={`ri-add-line text-sm transition-transform duration-200 ${open ? 'rotate-45' : 'rotate-0'}`} />
        </div>
      </button>

      {open && (
        <div
          className={`absolute bottom-full left-0 mb-2 z-[70] rounded-2xl overflow-hidden min-w-[180px] ${
            isDark
              ? 'bg-[#1C1D21]/95 border border-white/[0.05]'
              : 'bg-white/95 border border-black/[0.04]'
          }`}
          style={{
            backdropFilter: 'blur(20px) saturate(1.5)',
            WebkitBackdropFilter: 'blur(20px) saturate(1.5)',
            boxShadow: isDark
              ? '0 8px 32px rgba(0,0,0,0.45)'
              : '0 8px 32px rgba(0,0,0,0.08)',
            transformOrigin: 'bottom left',
          }}
        >
          <div className="flex flex-col py-1.5">
            {menuItems.map((item) => (
              <button
                key={item.label}
                onClick={() => handleMenuItemClick(item.action)}
                className={`flex items-center gap-3 px-3.5 py-2.5 cursor-pointer transition-colors duration-150 rounded-lg mx-1 ${
                  isDark
                    ? 'hover:bg-white/[0.04] text-[#A1A1A6]'
                    : 'hover:bg-black/[0.04] text-black/65'
                }`}
                style={{ touchAction: 'manipulation' }}
                type="button"
              >
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isDark ? 'bg-white/[0.04]' : 'bg-black/[0.03]'
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i
                      className={`${item.icon} text-[13px] ${
                        isDark ? 'text-[#6E6E73]' : 'text-black/35'
                      }`}
                    />
                  </div>
                </div>
                <span className="text-[13px] font-medium whitespace-nowrap">
                  {item.label}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}